import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  formatValueAuto,
  formatIndianInt,
  achDelta,
  DELTA_COLOR,
  FS_HEAD_SX,
  FS_CELL_SX,
} from './fieldSalesFormat.js';
import FieldSalesPager from './FieldSalesPager.jsx';
import { usePagedRows } from './fieldSalesPaging.js';

const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const BAD = '#DC2626';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';
const NO_DATA_TOOLTIP = 'No data source yet';

// Faint red tokens for the summary card (the reference uses a bold red strip;
// the task asks for a FAINT red summary card).
const RED_BG = 'rgba(220,38,38,.08)';
const RED_BORDER = 'rgba(220,38,38,.28)';

/** ₹ Cr/L (lakh input), em dash for non-positive. */
const money = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

const HEAD_SX = FS_HEAD_SX;
const CELL_SX = FS_CELL_SX;

/** Greyed Zone cell: the backend returns the state there, so Zone has no source. */
function ZoneCell() {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box
        component="span"
        aria-disabled="true"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.5, cursor: 'not-allowed' }}
      >
        —
        <Box
          component="span"
          sx={{
            px: '6px',
            py: '1px',
            borderRadius: '999px',
            bgcolor: '#eef1f5',
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: '9px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.3px',
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Box>
      </Box>
    </Tooltip>
  );
}

/** One stat cell inside the faint-red summary card (reference .crit-summary .c). */
function Stat({ label, value }) {
  return (
    <Box sx={{ px: '15px', py: '12px', borderRight: `1px solid ${RED_BORDER}`, '&:last-of-type': { borderRight: 'none' } }}>
      <Typography sx={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '.5px', color: BAD, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '19px', fontWeight: 800, color: '#8f1d1d', mt: '3px', lineHeight: 1.12 }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Critical HQ Watchlist tab (reference "Critical HQ Watchlist"). Subheading
 * "{Mon} — PCPM/TM < 50% of division & negative YoY", a faint red summary card
 * from the API summary, then the reference table (#, HQ, [Division], Zone, TMs,
 * Target, Sales, Ach %, PCPM/TM, vs Div, Growth %, Gap to Tgt) with a Total
 * row. Zone is a greyed "—". Values in Lakhs → ₹ Cr/L.
 *
 * @param {{ query: import('@tanstack/react-query').UseQueryResult, mon: string }} props
 */
export default function FieldSalesCriticalTab({ query, mon }) {
  const data = query?.data;
  const summary = data?.summary ?? {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const loading = query?.isLoading || query?.isFetching;

  const multiDiv = new Set(rows.map((r) => r.division)).size > 1;

  // Client-side pagination (50/page); resets when {Mon} or the row count changes.
  const paged = usePagedRows(rows, { resetKey: `${mon}:${rows.length}` });

  // Totals from rows (sum TMs/Target/Sales/Gap; Ach recomputed from the sums).
  const tTm = rows.reduce((s, r) => s + (Number(r.tm) || 0), 0);
  const tTgt = rows.reduce((s, r) => s + (Number(r.target) || 0), 0);
  const tSal = rows.reduce((s, r) => s + (Number(r.sales) || 0), 0);
  const tGap = rows.reduce((s, r) => s + (Number(r.gap) > 0 ? Number(r.gap) : 0), 0);
  const tAch = tTgt > 0 ? (tSal / tTgt) * 100 : NaN;
  const tGrowth = Number(summary.growth);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '16px 18px',
        mt: 1,
      }}
    >
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY, mb: '2px' }}>
        ⚠ Critical HQ Watchlist
      </Typography>
      <Typography sx={{ fontSize: '13px', color: MUTED, mb: '12px' }}>
        {mon} — PCPM/TM &lt; 50% of division &amp; negative YoY
      </Typography>

      {query?.isError ? (
        <Alert severity="error">Failed to load the critical watchlist.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            bgcolor: 'rgba(22,163,74,.10)',
            border: '1px solid rgba(22,163,74,.3)',
            color: '#0e7a37',
            borderRadius: '12px',
            p: '14px 16px',
            fontWeight: 600,
            fontSize: '13px',
          }}
        >
          ✓ No HQs are both below 50% of division PCPM/TM and declining for this period / filter.
          Nothing critical right now.
        </Box>
      ) : (
        <>
          {/* Faint red summary card. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(7,1fr)' },
              bgcolor: RED_BG,
              border: `1px solid ${RED_BORDER}`,
              borderRadius: '14px',
              overflow: 'hidden',
              mb: '14px',
            }}
          >
            <Stat label="No. of HQ" value={formatIndianInt(summary.hq_count)} />
            <Stat label="No. of TMs" value={formatIndianInt(summary.tm_count)} />
            <Stat label="Target" value={money(summary.target)} />
            <Stat label="Sales" value={money(summary.sales)} />
            <Stat label="Ach %" value={Number.isFinite(Number(summary.ach)) ? `${Number(summary.ach).toFixed(1)}%` : '—'} />
            <Stat label="PCPM / TM" value={Number.isFinite(Number(summary.pcpm_tm)) ? formatValueAuto(summary.pcpm_tm) : '—'} />
            <Stat
              label="Growth"
              value={Number.isFinite(Number(summary.growth)) ? `${Number(summary.growth) > 0 ? '+' : ''}${Number(summary.growth).toFixed(1)}%` : '—'}
            />
          </Box>

          {/* Note line (reference critNote). */}
          <Typography sx={{ fontSize: '12px', color: MUTED, mb: '10px' }}>
            <b>{formatIndianInt(summary.hq_count)}</b> HQs
            {Number.isFinite(Number(summary.pct_of_hqs)) ? ` (${Number(summary.pct_of_hqs).toFixed(0)}% of all HQs)` : ''}
            {' '}are unproductive and shrinking — they carry <b>{money(summary.sales)}</b> of sales
            {Number.isFinite(Number(summary.pct_of_sales)) ? ` (${Number(summary.pct_of_sales).toFixed(1)}% of division)` : ''}
            {' '}with a <b>{money(summary.total_gap)}</b> gap to target.
          </Typography>

          {/* Table. */}
          <TableContainer sx={{ maxHeight: 480, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_SX}>#</TableCell>
                  <TableCell sx={HEAD_SX}>HQ</TableCell>
                  {multiDiv ? <TableCell sx={HEAD_SX}>Division</TableCell> : null}
                  <TableCell sx={HEAD_SX}>Zone</TableCell>
                  <TableCell sx={HEAD_SX} align="right">TMs</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Target</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Sales</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Ach %</TableCell>
                  <TableCell sx={HEAD_SX} align="right">PCPM/TM</TableCell>
                  <TableCell sx={HEAD_SX} align="right">vs Div</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Growth %</TableCell>
                  <TableCell sx={HEAD_SX} align="right">Gap to Tgt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.pageRows.map((r, i) => (
                  <TableRow key={`${r.hq}-${r.division}-${i}`} hover>
                    <TableCell sx={CELL_SX}>{paged.offset + i + 1}</TableCell>
                    <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{r.hq ?? '—'}</TableCell>
                    {multiDiv ? <TableCell sx={CELL_SX}>{r.division ?? '—'}</TableCell> : null}
                    <TableCell sx={CELL_SX}><ZoneCell /></TableCell>
                    <TableCell sx={CELL_SX} align="right">{formatIndianInt(r.tm)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(r.target)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(r.sales)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Box component="span" sx={{ color: DELTA_COLOR[achDelta(r.ach)] }}>
                        {Number.isFinite(Number(r.ach)) ? `${Number(r.ach).toFixed(1)}%` : '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          px: '8px',
                          py: '2px',
                          borderRadius: '999px',
                          fontWeight: 600,
                          fontSize: '11.5px',
                          bgcolor: 'rgba(220,38,38,.12)',
                          color: BAD,
                          border: '1px solid rgba(220,38,38,.3)',
                        }}
                      >
                        {Number.isFinite(Number(r.pcpm_tm)) ? formatValueAuto(r.pcpm_tm) : '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Box component="span" sx={{ color: BAD }}>
                        {Number.isFinite(Number(r.vs_div)) ? `${Number(r.vs_div).toFixed(0)}%` : '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Box component="span" sx={{ color: BAD }}>
                        {Number.isFinite(Number(r.growth)) ? `${Number(r.growth).toFixed(1)}%` : '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{Number(r.gap) > 0 ? money(r.gap) : '—'}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { bgcolor: '#f8fafc', fontWeight: 700, borderTop: `2px solid ${BORDER}` } }}>
                  <TableCell sx={CELL_SX} colSpan={multiDiv ? 4 : 3}>
                    Total · {formatIndianInt(rows.length)} HQ{rows.length === 1 ? '' : 's'}
                  </TableCell>
                  <TableCell sx={CELL_SX} align="right">{formatIndianInt(tTm)}</TableCell>
                  <TableCell sx={CELL_SX} align="right">{money(tTgt)}</TableCell>
                  <TableCell sx={CELL_SX} align="right">{money(tSal)}</TableCell>
                  <TableCell sx={CELL_SX} align="right">
                    <Box component="span" sx={{ color: DELTA_COLOR[achDelta(tAch)] }}>
                      {Number.isFinite(tAch) ? `${tAch.toFixed(1)}%` : '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={CELL_SX} align="right" />
                  <TableCell sx={CELL_SX} align="right" />
                  <TableCell sx={CELL_SX} align="right">
                    <Box component="span" sx={{ color: BAD }}>
                      {Number.isFinite(tGrowth) ? `${tGrowth.toFixed(1)}%` : '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={CELL_SX} align="right">{money(tGap)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <FieldSalesPager page={paged.page} pageCount={paged.pageCount} total={paged.total} onChange={paged.setPage} unit="HQs" />
        </>
      )}
    </Box>
  );
}
